<template>
  <div class="landing">
    <div class="panel">
      <img class="logo" src="/deped-logo.svg" width="200" height="56" alt="Department of Education" />
      <h1 class="title">Office Performance Commitment and Review Form Management System</h1>

      <form class="form" @submit.prevent="onSubmit">
        <label class="label" for="uid">User id:</label>
        <input
          id="uid"
          v-model.trim="username"
          class="input"
          type="text"
          autocomplete="username"
          required
        />

        <label class="label" for="pwd">Password:</label>
        <input
          id="pwd"
          v-model="password"
          class="input"
          type="password"
          autocomplete="current-password"
          required
        />
        <div class="actions">
          <button class="btn-login" type="submit" :disabled="loading">
            {{ loading ? 'Please wait…' : 'Login' }}
          </button>
        </div>

        <p v-if="error" class="error" role="alert">{{ error }}</p>
      </form>
    </div>

    <!-- Custom Modal for empty School Head notification -->
    <div v-if="showSchoolHeadModal" class="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div class="modal-card">
        <header class="modal-header">
          <h2 id="modal-title" class="modal-title-text">School Head Name Required</h2>
        </header>
        <div class="modal-body">
          <p>
            The School Head name is currently empty for your account. Please enter the School Head's name in the <strong>Settings</strong> tab after continuing.
          </p>
          <p class="modal-notice-sub">
            *This is required to properly format and display grades in the generated OPCRF spreadsheet templates.*
          </p>
        </div>
        <footer class="modal-footer">
          <button type="button" class="btn-modal-close" @click="closeModalAndRedirect">
            Close &amp; Continue
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { setAuth, dashboardPathForRole } from '../auth/session'
import { clearSiteCookies } from '../utils/clearCookies'

export default {
  name: 'LoginView',
  mounted () {
    clearSiteCookies()
  },
  data () {
    return {
      username: '',
      password: '',
      loading: false,
      error: '',
      showSchoolHeadModal: false,
      pendingRedirectPath: ''
    }
  },
  methods: {
    async onSubmit () {
      this.error = ''
      this.loading = true
      clearSiteCookies()
      try {
        const res = await fetch('/api/login.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            username: this.username,
            password: this.password
          })
        })
        const raw = await res.text()
        let data = {}
        try {
          data = raw ? JSON.parse(raw) : {}
        } catch {
          this.error = raw.trim().slice(0, 300) || 'Login failed (invalid server response).'
          return
        }
        if (res.status === 431) {
          this.error = 'Browser sent too many cookies. Clear site data for localhost, then try again.'
          return
        }
        if (!res.ok || !data.ok) {
          this.error = data.message || 'Login failed. Please try again.'
          return
        }
        const u = data.user
        setAuth({
          personnelid: u.personnelid,
          fname: u.fname,
          mname: u.mname,
          lname: u.lname,
          roleid: u.roleid,
          username: u.username,
          schoolid: u.schoolid != null ? u.schoolid : null,
          schoolname: u.schoolname != null ? u.schoolname : null,
          assigned_folders: u.assigned_folders != null ? u.assigned_folders : 'all'
        })
        if (Number(u.roleid) === 2 && (!u.schoolhead || u.schoolhead.trim() === '')) {
          this.pendingRedirectPath = dashboardPathForRole(u.roleid)
          this.showSchoolHeadModal = true
        } else {
          await this.$router.replace(dashboardPathForRole(u.roleid))
        }
      } catch {
        this.error = 'Cannot reach the server. Is PHP running on port 8081?'
      } finally {
        this.loading = false
      }
    },
    async closeModalAndRedirect () {
      this.showSchoolHeadModal = false
      if (this.pendingRedirectPath) {
        await this.$router.replace(this.pendingRedirectPath)
      }
    }
  }
}
</script>

<style scoped>
.landing {
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right))
    max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
  box-sizing: border-box;
}

.landing::before {
  content: '';
  position: fixed;
  inset: 0;
  z-index: -1;
  background:
    linear-gradient(rgba(0, 0, 0, 0.58), rgba(0, 0, 0, 0.58)),
    url('~@/assets/login-bg.png') center / cover no-repeat;
}

.panel {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  background: #2b6cb0;
  padding: 24px 22px 32px;
  border-radius: 4px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
}

@media (min-width: 420px) {
  .panel {
    padding: 28px 32px 36px;
  }
}

.logo {
  display: block;
  margin: 0 auto 16px;
  transform: translateX(30px);
}

.title {
  margin: 0 0 24px;
  color: #fff;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(0.78rem, 2.8vw, 0.95rem);
  font-weight: 700;
  line-height: 1.35;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.form {
  text-align: left;
}

.label {
  display: block;
  color: #fff;
  font-size: 0.8rem;
  margin-bottom: 6px;
}

.input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  margin-bottom: 14px;
  border: none;
  border-radius: 2px;
  background: #e8e8e8;
  font-size: 0.95rem;
}

.input:focus {
  outline: 2px solid #fff;
  outline-offset: 1px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.register-line {
  margin: -4px 0 10px;
}

.register-link {
  color: #e6f2ff;
  font-size: 0.82rem;
  text-decoration: underline;
}

.register-link:hover {
  color: #ffffff;
}

.btn-login {
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  background: #fff;
  color: #1a202c;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.btn-login:hover:not(:disabled) {
  background: #f7fafc;
}

.btn-login:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.error {
  margin: 14px 0 0;
  color: #fed7d7;
  font-size: 0.85rem;
  text-align: center;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-card {
  background: #ffffff;
  border-radius: 12px;
  width: min(440px, 90vw);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: modal-fadeIn 0.25s ease-out;
}

@keyframes modal-fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.modal-header {
  padding: 16px 20px;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
}

.modal-title-text {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #2d3748;
}

.modal-body {
  padding: 20px;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #4a5568;
  text-align: left;
}

.modal-notice-sub {
  margin-top: 12px;
  font-size: 0.8rem;
  color: #718096;
  font-style: italic;
}

.modal-footer {
  padding: 14px 20px;
  background: #f7fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

.btn-modal-close {
  padding: 8px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #ffffff;
  background: #3182ce;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-modal-close:hover {
  background: #2b6cb0;
}
</style>
